
import Vue from "vue";
import { getBrandConfig } from "@/config/brands";
import GameMaterialModal from "@/components/management/GameMaterialModal.vue";
import SideBar from "@/components/SideBar.vue";
import { getCurrentUser } from "@/auth";
import {
  GameMaterials,
  GameEntry,
} from "@/components/management/types";

type ManagementStore = Record<string, GameMaterials>;

const DEFAULT_MATERIALS: GameMaterials = {
  demoLink: null,
  gdd: null,
  cad: null,
  marketing: [],
};

export default Vue.extend({
  name: "ManagementView",

  components: {
    GameMaterialModal,
    SideBar,
  },

  data() {
    return {
      selectedBrandId: null as string | null,
      gameSearch: "" as string,
      allGames: [] as GameEntry[],
      materials: {} as ManagementStore,
      modalGame: null as GameEntry | null,
      mobileMenuOpen: false,
      currentBrandId: getCurrentUser()?.brand || "",
      mobileMenuHandler: null as ((isOpen: boolean) => void) | null,
    };
  },

  computed: {
    brandOptions(): Array<{ id: string; name: string }> {
      try {
        const config = getBrandConfig('jogoglobal-admin');
        return config.categories.map(c => ({ id: c.name, name: c.name }));
      } catch {
        return [];
      }
    },

    filteredGames(): GameEntry[] {
      let games = this.allGames;
      if (this.selectedBrandId) {
        games = games.filter(g => g.categoryName === this.selectedBrandId);
      }
      const q = this.gameSearch.toLowerCase().trim();
      if (!q) return games;
      return games.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.categoryName.toLowerCase().includes(q)
      );
    },
  },

  mounted() {
    this.loadJogoglobalAdmin();
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.height = "";
    document.body.style.width = "";
    this.mobileMenuHandler = (isOpen: boolean) => {
      this.mobileMenuOpen = isOpen;
      document.body.style.overflow = isOpen ? "hidden" : "";
    };
    this.$root.$on("mobile-menu-toggle", this.mobileMenuHandler);
  },

  beforeDestroy() {
    if (this.mobileMenuHandler) {
      this.$root.$off("mobile-menu-toggle", this.mobileMenuHandler);
    }
  },

  methods: {
    closeMobileSidebar() {
      this.mobileMenuOpen = false;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.height = "";
      document.body.style.width = "";
    },

    onSidebarGameSelected() {
      this.$router.push("/dashboard");
      this.closeMobileSidebar();
    },

    onSidebarReset() {
      this.$router.push("/dashboard");
      this.closeMobileSidebar();
    },

    loadCategoryEntries(brandId: string, category: any): GameEntry[] {
      let raw: any;
      try {
        raw = require(`@/data/${category.gamesPath}`);
      } catch {
        return [];
      }
      const games: any[] = Array.isArray(raw)
        ? raw
        : category.ipKeys?.length
        ? category.ipKeys.flatMap((k: string) => raw[k] ?? [])
        : Object.values(raw).flat();

      const entries: GameEntry[] = [];
      for (const game of games) {
        const key = `${brandId}||${category.name}||${game.name}`;
        entries.push({
          key,
          name: game.name,
          categoryName: category.name,
          brandId,
        });
        this.seedMaterialsFromGameFiles(key, game.gameFiles || []);
      }
      return entries;
    },

    loadJogoglobalAdmin() {
      try {
        const config = getBrandConfig('jogoglobal-admin');
        const entries: GameEntry[] = [];
        for (const category of config.categories) {
          entries.push(...this.loadCategoryEntries('jogoglobal-admin', category));
        }
        this.allGames = entries;
      } catch {
        this.allGames = [];
      }
    },

    getMaterials(key: string): GameMaterials {
      return this.materials[key] || { ...DEFAULT_MATERIALS, marketing: [] };
    },

    saveMaterials(key: string, data: GameMaterials) {
      this.$set(this.materials, key, data);
    },

    getCompletion(key: string): number {
      const m = this.getMaterials(key);
      let filled = 0;
      if (m.demoLink) filled++;
      if (m.gdd) filled++;
      if (m.cad) filled++;
      if (m.marketing.length > 0) filled++;
      return Math.round((filled / 4) * 100);
    },

    openModal(game: GameEntry) {
      this.modalGame = game;
    },

    onSave(updatedMaterials: GameMaterials) {
      if (this.modalGame) {
        this.saveMaterials(this.modalGame.key, updatedMaterials);
      }
      this.modalGame = null;
    },

    // ─── TEMPORARY MOCK ADAPTER ────────────────────────────────────────────────
    // Reads existing gameFiles from each game JSON and maps them to GameMaterials.
    // Remove this method (and its call in loadCategoryEntries) when real DB is wired.
    seedMaterialsFromGameFiles(key: string, gameFiles: any[]): void {
      if (!gameFiles || gameFiles.length === 0) return;
      if (this.materials[key]) return; // don't overwrite user edits

      const result: GameMaterials = {
        demoLink: null,
        gdd: null,
        cad: null,
        marketing: [],
      };

      for (const f of gameFiles) {
        const nameLower = (f.name || "").toLowerCase();
        const hasContent = f.actionType !== "disabled" && (f.url || f.gameId);

        if (!hasContent) continue;

        if (nameLower.includes("demo")) {
          result.demoLink = f.url || `[api-launch: ${f.gameId}]`;
        } else if (f.type === "Playable" && !result.demoLink) {
          result.demoLink = f.url || `[api-launch: ${f.gameId}]`;
        } else if (
          nameLower.includes("design document") ||
          nameLower.includes("gdd")
        ) {
          result.gdd = { name: f.name, type: "PDF" };
        } else if (
          nameLower.includes("cad") ||
          nameLower.includes("concept approval")
        ) {
          result.cad = { name: f.name, type: "PDF" };
        } else if (
          nameLower.includes("marketing") ||
          nameLower.includes("slick") ||
          nameLower.includes("latest art") ||
          nameLower.includes("project update") ||
          nameLower.includes("project summary")
        ) {
          const ext = f.url
            ? f.url.split(".").pop()?.toUpperCase() || "FILE"
            : "FILE";
          result.marketing.push({ name: f.name, type: ext });
        }
      }

      const hasAny =
        result.demoLink ||
        result.gdd ||
        result.cad ||
        result.marketing.length > 0;
      if (hasAny) this.$set(this.materials, key, result);
    },
    // ─── END TEMPORARY MOCK ADAPTER ────────────────────────────────────────────
  },
});
